// Regular expressions for validation
export const validationRegex = {
  email:
    // eslint-disable-next-line
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i,
};

const organisationNameVariants = [
  'organisation',
  'organisation name',
  'organisationname',
  'organisations',
  'organisation names',
  'organisationnames',
  'organization',
  'organization name',
  'organizationname',
  'organizations',
  'organization names',
  'organizationnames',
  'company',
  'companies',
  'org',
  'orgs',
  'org name',
  'orgname',
  'org names',
  'orgnames',
  'org_name',
  'org_names',
  'org_name',
  'name',
  'names',
  'name of organisation',
  'names of organisation',
];

// Field configurations
/**
 * Field configurations
 * @type {Record<string, {required: boolean, label: string, type: string, validate: (value: string) => {isValid: boolean, reason?: string}, testColumn: ((columnData: string[], includeHeaders: boolean) => boolean)[]}>}
 */
export const fieldConfigs = {
  email: {
    required: true,
    label: 'Email',
    type: 'email',
    validate: value => {
      if (!value) return { isSkipped: true };
      if (!validationRegex.email.test(value)) {
        return { isValid: false, reason: 'Invalid email format' };
      }
      if (value.length > 320) {
        return { isValid: false, reason: 'Email is too long' };
      }
      return { isValid: true };
    },
    /**
     * Test if the column data is an email column
     * @param {string[]?} columnData
     * @param {boolean} includeHeaders
     * @returns {boolean}
     */
    testColumn: [
      (columnData, includeHeaders) => {
        if (!columnData) {
          return false;
        }

        if (
          !includeHeaders &&
          (columnData[0]?.toLowerCase() === 'email' ||
            columnData[0]?.toLowerCase() === 'emails')
        ) {
          return true;
        }
        return false;
      },
      (columnData, includeHeaders) => {
        if (!columnData) {
          return false;
        }

        if (
          columnData.filter(x => `${x}`.includes('@')).length /
            columnData.length >
          0.6
        ) {
          return true;
        }
        return false;
      },
    ],
  },
  organisation: {
    required: true,
    label: 'Organisation',
    type: 'organisation',
    validate: value => {
      if (!value) return { isSkipped: true };
      if (value.length > 255) {
        return { isValid: false, reason: 'Organisation is too long' };
      }
      return { isValid: true };
    },
    /**
     * Test if the column data is an organisation column
     * @param {string[]?} columnData
     * @param {boolean} includeHeaders
     * @returns {boolean}
     */
    testColumn: [
      (columnData, includeHeaders) => {
        if (!columnData) {
          return false;
        }
        if (
          !includeHeaders &&
          organisationNameVariants.includes(columnData[0]?.toLowerCase())
        ) {
          return true;
        }
        return false;
      },
    ],
  },
};

/**
 * Get required field IDs
 * @returns {string[]}
 */
export const getRequiredFields = type => {
  return Object.entries(fieldConfigs)
    .filter(([_, config]) => config.required && config.type === type)
    .map(([fieldId]) => fieldId);
};

/**
 * Validate a single field
 * @param {string} fieldId
 * @param {string} value
 * @returns {boolean}
 */
const validateField = (fieldId, value) => {
  const config = fieldConfigs[fieldId];
  if (!config) return { isValid: true };
  return config.validate(value);
};

/**
 * Validate fields in a column
 * @param {string} fieldId
 * @param {string} column
 * @param {string[][]} data
 * @param {boolean} includeHeaders
 */
export const validateFields = (fieldId, column, data, includeHeaders) => {
  if (!column) return;

  const results = {
    total: 0,
    valid: [],
    invalid: [],
  };

  data.forEach((row, index) => {
    if (!includeHeaders && index === 0) {
      return;
    }
    results.total++;

    const value = row[column];
    const validation = validateField(fieldId, value);

    if (validation.isSkipped) {
      return;
    }

    if (validation.isValid) {
      results.valid.push({
        rowNumber: index + 1,
        value,
      });
    } else {
      results.invalid.push({
        rowNumber: index + 1,
        value,
        reason: validation.reason,
      });
    }
  });

  return results;
};
